import { useState, useEffect } from 'react';
import { instrumentAPI } from '../../../../../services';
import './TradingHoursModal.css';

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Hong_Kong', 'Asia/Singapore', 'Australia/Sydney'
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const HOLIDAY_TYPES = [
  { value: 'full_closure', label: 'Full Closure' },
  { value: 'early_closure', label: 'Early Closure' },
  { value: 'late_opening', label: 'Late Opening' }
];

function TradingHoursModal({ instrument, onClose, onSave }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('manual');
  const [presets, setPresets] = useState([]);
  const [presetCategories, setPresetCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [applyingPreset, setApplyingPreset] = useState(false);
  const [tradingHours, setTradingHours] = useState({
    timezone: 'UTC', weekly_schedule: [], holidays: []
  });

  const ENABLE_TRADING_HOURS_PRESETS = true;

  useEffect(() => {
    if (instrument) {
      fetchTradingHours();
      if (ENABLE_TRADING_HOURS_PRESETS) { fetchPresets(); fetchPresetCategories(); }
    }
  }, [instrument]);

  const fetchTradingHours = async () => {
    setLoading(true); setError(null);
    try {
      const res = await instrumentAPI.getTradingHours(instrument.symbol);
      if (res.data?.trading_hours) {
        const d = res.data.trading_hours;
        setTradingHours({ ...d, weekly_schedule: (d.weekly_schedule || []).filter(Boolean), holidays: d.holidays || [] });
      } else {
        setTradingHours(getDefaults(instrument.type));
      }
    } catch (err) {
      if (err.response?.status === 404) setTradingHours(getDefaults(instrument.type));
      else setError('Failed to load trading hours');
    } finally { setLoading(false); }
  };

  const fetchPresets = async () => {
    try {
      const res = await instrumentAPI.getTradingHoursPresets();
      setPresets(res.data?.presets || []);
    } catch { setPresets([]); }
  };

  const fetchPresetCategories = async () => {
    try {
      const res = await instrumentAPI.getPresetCategories();
      setPresetCategories(['all', ...(res.data?.categories || ['forex','stocks','crypto','commodities','indices','custom'])]);
    } catch { setPresetCategories(['all','forex','stocks','crypto','commodities','indices','custom']); }
  };

  const getDefaults = (type) => {
    const t = (type || '').toLowerCase();
    if (t === 'crypto') return { timezone: 'UTC', weekly_schedule: DAYS.map(d => ({ day: d, sessions: [{ open: '00:00', close: '23:59', name: '24/7' }] })), holidays: [] };
    if (t === 'stocks') return { timezone: 'America/New_York', weekly_schedule: ['monday','tuesday','wednesday','thursday','friday'].map(d => ({ day: d, sessions: [{ open: '09:30', close: '16:00', name: 'Regular' }] })), holidays: [] };
    return { timezone: 'UTC', weekly_schedule: ['monday','tuesday','wednesday','thursday','friday'].map(d => ({ day: d, sessions: [{ open: '00:00', close: '23:59', name: '24h' }] })), holidays: [] };
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try { await instrumentAPI.updateTradingHours(instrument.symbol, tradingHours); onSave(); onClose(); }
    catch (err) { setError('Failed to save'); }
    finally { setSaving(false); }
  };

  // ── Schedule helpers ──
  const setSchedule = (fn) => setTradingHours(p => ({ ...p, weekly_schedule: fn(p.weekly_schedule || []) }));

  const addSession = (dayIdx) => {
    const day = DAYS[dayIdx];
    setSchedule(sch => {
      const i = sch.findIndex(d => d?.day === day);
      if (i >= 0) {
        const s = [...sch]; s[i] = { ...s[i], sessions: [...s[i].sessions, { open:'09:00', close:'17:00', name:'' }] }; return s;
      }
      return [...sch, { day, sessions: [{ open:'09:00', close:'17:00', name:'' }] }];
    });
  };

  const removeSession = (dayIdx, sessIdx) => {
    const day = DAYS[dayIdx];
    setSchedule(sch => {
      const i = sch.findIndex(d => d?.day === day);
      if (i < 0) return sch;
      const s = [...sch]; s[i] = { ...s[i], sessions: s[i].sessions.filter((_, j) => j !== sessIdx) };
      return s;
    });
  };

  const updateSession = (dayIdx, sessIdx, field, val) => {
    const day = DAYS[dayIdx];
    setSchedule(sch => {
      const i = sch.findIndex(d => d?.day === day);
      if (i < 0) return sch;
      const s = [...sch]; s[i] = { ...s[i], sessions: s[i].sessions.map((ss, j) => j === sessIdx ? { ...ss, [field]: val } : ss) };
      return s;
    });
  };

  // ── Holiday helpers ──
  const addHoliday = () => setTradingHours(p => ({ ...p, holidays: [...p.holidays, { date: new Date().toISOString().slice(0,10), name: '', type: 'full_closure' }] }));
  const removeHoliday = (i) => setTradingHours(p => ({ ...p, holidays: p.holidays.filter((_, j) => j !== i) }));
  const updateHoliday = (i, f, v) => setTradingHours(p => { const h = [...p.holidays]; h[i] = { ...h[i], [f]: v }; return { ...p, holidays: h }; });

  // ── Apply preset ──
  const applyPreset = async (preset) => {
    setApplyingPreset(true); setError(null);
    try { await instrumentAPI.applyPresetToInstrument(instrument.symbol, preset.name); await fetchTradingHours(); setActiveTab('manual'); onSave(); }
    catch (err) { setError(err.message); }
    finally { setApplyingPreset(false); }
  };

  const filteredPresets = selectedCategory === 'all' ? presets : presets.filter(p => (p.category || '').toLowerCase() === selectedCategory);

  if (!instrument) return null;

  return (
    <div className="trading-hours-overlay">
      <div className="trading-hours-modal">

        {/* ── Header ── */}
        <div className="th-header">
          <div className="th-header-left">
            <div className="th-header-icon"><i className="fas fa-clock"></i></div>
            <div className="th-header-info">
              <h2>Trading Hours</h2>
              <span>{instrument.symbol} — {instrument.description}</span>
            </div>
          </div>
          <button className="th-close" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>

        {/* ── Tabs ── */}
        {ENABLE_TRADING_HOURS_PRESETS && (
          <div className="th-tabs">
            <button className={`th-tab ${activeTab === 'presets' ? 'active' : ''}`} onClick={() => setActiveTab('presets')}>
              <i className="fas fa-magic"></i> Presets
            </button>
            <button className={`th-tab ${activeTab === 'manual' ? 'active' : ''}`} onClick={() => setActiveTab('manual')}>
              <i className="fas fa-cogs"></i> Manual
            </button>
          </div>
        )}

        {/* ── Body ── */}
        <div className="th-body">
          {loading ? (
            <div className="th-loading"><div className="th-spinner"></div><span>Loading…</span></div>
          ) : (
            <>
              {error && <div className="th-error"><i className="fas fa-exclamation-triangle"></i>{error}</div>}

              {activeTab === 'manual' ? (
                <>
                  {/* Timezone */}
                  <div className="th-card">
                    <div className="th-card-head"><i className="fas fa-globe"></i><h3>Timezone</h3></div>
                    <div className="th-card-body">
                      <div className="th-field">
                        <select value={tradingHours.timezone} onChange={e => setTradingHours(p => ({ ...p, timezone: e.target.value }))}>
                          {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Weekly Schedule — TABLE */}
                  <div className="th-card">
                    <div className="th-card-head"><i className="fas fa-calendar-week"></i><h3>Weekly Schedule</h3></div>
                    <table className="th-schedule-table">
                      <thead>
                        <tr>
                          <th>Day</th>
                          <th>Sessions</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {DAYS.map((day, dayIdx) => {
                          const daySch = (tradingHours.weekly_schedule || []).find(d => d?.day === day);
                          const sessions = daySch?.sessions || [];
                          return (
                            <tr key={day} className="th-day-row">
                              <td className="th-day-name">
                                {day} <span className="th-day-count">{sessions.length}</span>
                              </td>
                              <td>
                                {sessions.length === 0 ? (
                                  <span className="th-day-empty">No sessions</span>
                                ) : (
                                  sessions.map((sess, sIdx) => (
                                    <div key={sIdx} className="th-session-mini">
                                      <input
                                        className="th-session-name-input"
                                        placeholder="Name"
                                        value={sess.name || ''}
                                        onChange={e => updateSession(dayIdx, sIdx, 'name', e.target.value)}
                                      />
                                      <input
                                        type="time" className="th-time-input"
                                        value={sess.open || ''}
                                        onChange={e => updateSession(dayIdx, sIdx, 'open', e.target.value)}
                                      />
                                      <span className="th-time-arrow">→</span>
                                      <input
                                        type="time" className="th-time-input"
                                        value={sess.close || ''}
                                        onChange={e => updateSession(dayIdx, sIdx, 'close', e.target.value)}
                                      />
                                      <button className="th-remove-session" onClick={() => removeSession(dayIdx, sIdx)} title="Remove session">
                                        <i className="fas fa-times"></i>
                                      </button>
                                    </div>
                                  ))
                                )}
                                <button className="th-add-session" onClick={() => addSession(dayIdx)}>
                                  <i className="fas fa-plus"></i> Add
                                </button>
                              </td>
                              <td></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Holidays */}
                  <div className="th-card">
                    <div className="th-card-head"><i className="fas fa-calendar-times"></i><h3>Holidays</h3></div>
                    <div className="th-card-body">
                      {(tradingHours.holidays || []).length === 0 ? (
                        <div className="th-empty-holidays">
                          <i className="fas fa-calendar-check"></i>
                          No holidays configured
                        </div>
                      ) : (
                        (tradingHours.holidays || []).map((h, i) => (
                          <div key={i} className="th-holiday-item">
                            <div className="th-holiday-row">
                              <div className="th-field date-field">
                                <label>Date</label>
                                <input type="date" value={h.date || ''} onChange={e => updateHoliday(i, 'date', e.target.value)} />
                              </div>
                              <div className="th-field">
                                <label>Name</label>
                                <input type="text" placeholder="e.g. Christmas" value={h.name || ''} onChange={e => updateHoliday(i, 'name', e.target.value)} />
                              </div>
                              <div className="th-field type-field">
                                <label>Type</label>
                                <select value={h.type || 'full_closure'} onChange={e => updateHoliday(i, 'type', e.target.value)}>
                                  {HOLIDAY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                              </div>
                              <button className="th-remove-holiday" onClick={() => removeHoliday(i)} title="Remove"><i className="fas fa-trash"></i></button>
                            </div>
                            {(h.type === 'early_closure' || h.type === 'late_opening') && (
                              <div className="th-modified-row">
                                <div className="th-field">
                                  <label>{h.type === 'late_opening' ? 'Opens at' : 'Closes at'}</label>
                                  <input type="time" value={h.modified_time || ''} onChange={e => updateHoliday(i, 'modified_time', e.target.value)} />
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                      <button className="th-add-holiday" onClick={addHoliday}><i className="fas fa-plus"></i> Add Holiday</button>
                    </div>
                  </div>
                </>
              ) : (
                /* ── Presets Tab ── */
                <div className="th-card">
                  <div className="th-card-head"><i className="fas fa-magic"></i><h3>Quick Setup Presets</h3></div>
                  <div className="th-card-body">
                    {presetCategories.length > 1 && (
                      <div className="th-preset-filter">
                        {presetCategories.map(cat => (
                          <button key={cat} className={`th-filter-chip ${selectedCategory === cat ? 'active' : ''}`} onClick={() => setSelectedCategory(cat)}>
                            {cat === 'all' ? 'All' : cat}
                          </button>
                        ))}
                      </div>
                    )}
                    {filteredPresets.length === 0 ? (
                      <div className="th-empty-holidays"><i className="fas fa-inbox"></i>No presets available</div>
                    ) : (
                      <div className="th-preset-list">
                        {filteredPresets.map(p => (
                          <div key={p._id || p.name} className="th-preset-item">
                            <div className="th-preset-info">
                              <h4>{p.name}</h4>
                              {p.description && <p>{p.description}</p>}
                              <div className="th-preset-meta">
                                {p.category && <span className={`th-preset-cat ${p.category.toLowerCase()}`}>{p.category}</span>}
                                {p.is_system && <span className="th-preset-badge">System</span>}
                              </div>
                            </div>
                            <button className="th-btn-sm th-btn-sm-primary" onClick={() => applyPreset(p)} disabled={applyingPreset}>
                              {applyingPreset ? 'Applying…' : 'Apply'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="th-footer">
          <div className="th-footer-hint">
            <i className="fas fa-info-circle"></i>
            <span>Timezone: {tradingHours.timezone}</span>
          </div>
          <div className="th-footer-actions">
            <button className="th-btn th-btn-cancel" onClick={onClose}>Cancel</button>
            <button className="th-btn th-btn-save" onClick={handleSave} disabled={saving}>
              {saving ? <><i className="fas fa-spinner fa-spin"></i>Saving…</> : <><i className="fas fa-save"></i>Save</>}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

export default TradingHoursModal;
